import { useCallback } from 'react';

import { useChainId } from '@occc-app/Chain/hooks';
import { GeneralSurveyFragment } from '@occc/graphql';

import { useCreateSurveyOffer } from '../../Wallet/hooks';
import { convertFormFieldsToCreateInput } from '../utils/convertFormFieldsToCreateInput';
import { GeneralSurveyConfigureFormValues } from '../components/ConfigureForm/types';
import { GeneralSurveyTargetFormValues } from '../components/TargetForm/types';
import { GeneralSurveyScheduleFormValues } from '../components/ScheduleForm/types';
import { useGeneralSurveyCreate } from '../../../dataAccessLayer/survey/mutations';

type UseCreateGeneralSurveyParams = {
  onCompleted: (params: { survey: GeneralSurveyFragment; isNew: boolean }) => void;
  onError: (params: { isIncentivizeError: boolean; survey?: GeneralSurveyFragment }) => void;
};

type CreateGeneralSurveyParams = {
  configureFormValues: GeneralSurveyConfigureFormValues;
  targetFormValues: GeneralSurveyTargetFormValues;
  scheduleFormValues: GeneralSurveyScheduleFormValues;
};

export const useCreateSurvey = ({ onError, onCompleted }: UseCreateGeneralSurveyParams) => {
  const chainId = useChainId();
  const [generalSurveyCreateMutation, { loading: createSurveyLoading }] = useGeneralSurveyCreate();
  const { createSurveyOffer, loading: createIncentivizeResponsesLoading } = useCreateSurveyOffer();

  const createSurvey = useCallback(
    async ({
      configureFormValues,
      targetFormValues,
      scheduleFormValues,
    }: CreateGeneralSurveyParams) => {
      const input = convertFormFieldsToCreateInput({
        configure: configureFormValues,
        target: targetFormValues,
        schedule: scheduleFormValues,
        chainId,
      });

      const { data } = await generalSurveyCreateMutation({ variables: { input } });

      if (!data?.generalSurveyCreate.uuid) {
        onError({ isIncentivizeError: false });
        return;
      }

      if (!configureFormValues.incentivizeResponses.isEnabled) {
        onCompleted({ survey: data.generalSurveyCreate, isNew: true });
        return;
      }

      // TODO: merge incentivize code with Wallet Steering Incentivize code, they are almost the same
      const { errors } = await createSurveyOffer({
        surveyUuid: data.generalSurveyCreate.uuid,
        values: configureFormValues.incentivizeResponses,
      });

      if (errors?.length) {
        onError({ isIncentivizeError: true });
        return;
      }

      onCompleted({ survey: data.generalSurveyCreate, isNew: true });
    },
    [chainId, createSurveyOffer, generalSurveyCreateMutation, onCompleted, onError],
  );

  return {
    createSurvey,
    loading: createSurveyLoading || createIncentivizeResponsesLoading,
  };
};
